import { Injectable, computed, signal, effect, inject } from '@angular/core';

import { CapyStatsService } from './capystats.service';

const STORAGE_KEY = 'nadmorzem_favorites';

@Injectable({ providedIn: 'root' })
export class FavoritesService {
  private readonly capyStats = inject(CapyStatsService);
  private readonly _favorites = signal<number[]>(this.loadFromStorage());

  readonly favorites = this._favorites.asReadonly();
  readonly count = computed(() => this._favorites().length);

  constructor() {
    effect(() => {
      this.saveToStorage(this._favorites());
    });
  }

  isFavorite(propertyId: number): boolean {
    return this._favorites().includes(propertyId);
  }

  toggle(propertyId: number): void {
    const adding = !this._favorites().includes(propertyId);
    this._favorites.update(favorites => {
      if (favorites.includes(propertyId)) {
        return favorites.filter(id => id !== propertyId);
      }
      return [...favorites, propertyId];
    });
    if (adding) {
      this.trackAdded(propertyId);
    }
  }

  add(propertyId: number): void {
    if (!this.isFavorite(propertyId)) {
      this._favorites.update(favorites => [...favorites, propertyId]);
      this.trackAdded(propertyId);
    }
  }

  /**
   * NO-PII: only the property id. Fires once per add (absent→present), so every
   * heart-toggle entry point that routes through {@link toggle}/{@link add} is
   * covered from a single place.
   */
  private trackAdded(propertyId: number): void {
    this.capyStats.track('FavoriteAdded', { propertyId });
  }

  remove(propertyId: number): void {
    this._favorites.update(favorites =>
      favorites.filter(id => id !== propertyId)
    );
  }

  clear(): void {
    this._favorites.set([]);
  }

  private loadFromStorage(): number[] {
    if (typeof localStorage === 'undefined') return [];

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          return parsed.filter((id): id is number => typeof id === 'number');
        }
      }
    } catch {
      console.warn('Failed to load favorites from localStorage');
    }
    return [];
  }

  private saveToStorage(favorites: number[]): void {
    if (typeof localStorage === 'undefined') return;

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites));
    } catch {
      console.warn('Failed to save favorites to localStorage');
    }
  }
}
